import PropTypes from 'prop-types';
import { useForm } from 'react-final-form';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import useI18n from '../../../../lib/useI18n';
import { PlaceholderQuill, StyledInputWrapper, StyledQuill } from './styles';

import { HiddenInput } from '../../styles/form';
import { Field } from '../field';
import { getError } from '../utils';
import { useEditorValues } from './use_editor_values';
import { generateModules, generateTranslations, setup } from './setup';

/**
 * Editor component renders wysiwyg editor. its value can be
 * rendered as html. The list of editing commands can be found
 * in the ./utils.js TOOLBAR const.
 *
 *   className: Additional CSS class name for the component.
 *   disabled: Determines whether the editor is disabled or not.
 *   errorText: The error message to display, if any.
 *   hint: A popup text for the help icon in the top right.
 *   hintId: An id for the help icon button wrapper.
 *   initialValue: The initial value of the editor.
 *   inputAttrs: Additional attributes to be applied to the hidden input element.
 *   label: The label text for the editor.
 *   name: The name of the input field.
 *   namespace: For generating tracking id for potential error messages.
 *   placeholder: The placeholder text for the editor.
 *   required: affect how the label will look.
 */

setup(ReactQuill.Quill);
const modules = generateModules(ReactQuill.Quill);

export const Editor = ({
  className,
  disabled,
  errorText,
  form,
  hint,
  hintId,
  inputAttrs,
  label,
  labelProps,
  name,
  namespace,
  onBlur,
  onChange,
  onFocus,
  placeholder,
  required,
  value: _value,
}) => {
  const initialValue = form.getState().initialValues[name];
  const { translate } = useI18n('app.form.editor');
  const {
    handleOnBlur,
    handleOnChange,
    handleOnFocus,
    isActive,
    isExpanded,
    quillRef,
    setIsActive,
    showPlaceholder,
    value,
  } = useEditorValues({
    _value,
    placeholder,
    form,
    onChange,
    name,
    onBlur,
    onFocus,
  });
  return (
    <StyledInputWrapper
      className={className}
      errorText={errorText}
      name={name}
      namespace={namespace}
      hint={hint}
      hintId={hintId}
      labelProps={{ label, required, ...labelProps }}
      onBlur={onBlur}
    >
      <div
        onFocus={() => {
          setIsActive(true);
        }}
      >
        {/* Quill does not support html as a placeholder, so we had to
          implement two versions of the editor: one will be utilized
          as a placeholder that shows html, the other one is 'real' editor */}
        <PlaceholderQuill
          disabled={disabled}
          hide={!showPlaceholder || !placeholder}
          id={`${name}_placeholder`}
          modules={{ toolbar: { container: [] } }}
          value={placeholder}
        />
      </div>
      <StyledQuill
        className="quillEditor"
        disabled={disabled}
        hide={showPlaceholder}
        id={`${name}_editor`}
        isActive={isActive}
        isExpanded={isExpanded}
        modules={modules}
        onBlur={handleOnBlur}
        onChange={handleOnChange}
        onFocus={handleOnFocus}
        ref={quillRef}
        theme={'snow'}
        translations={generateTranslations({ translate })}
        defaultValue={initialValue}
        key={initialValue}
      />
      <HiddenInput
        {...inputAttrs}
        readOnly
        disabled={disabled}
        id={name}
        name={name}
        onFocus={() => {
          // we take focus from FocusOnError, and handle it so that
          // the quill editor becomes active
          setIsActive(true);
        }}
        value={value}
      />
    </StyledInputWrapper>
  );
};

Editor.defaultProps = {
  errorText: null,
};

Editor.propTypes = {
  className: PropTypes.string,
  disabled: PropTypes.bool,
  errorText: PropTypes.string,
  hint: PropTypes.string,
  hintId: PropTypes.string,
  inputAttrs: PropTypes.object,
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  namespace: PropTypes.string,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  value: PropTypes.string,
};

export const EditorField = ({
  className,
  disabled,
  hint,
  hintId,
  inputAttrs,
  label,
  labelProps,
  name,
  namespace,
  placeholder,
  required,
  ...fieldProps
}) => {
  const form = useForm();
  return (
    <Field {...fieldProps} name={name} required={required}>
      {({ input, meta }) => (
        <Editor
          {...input}
          className={className}
          disabled={disabled}
          errorText={getError(meta)}
          form={form}
          hint={hint}
          hintId={hintId}
          inputAttrs={inputAttrs}
          labelProps={{ label, required, ...labelProps }}
          namespace={namespace}
          placeholder={placeholder}
          required={required}
        />
      )}
    </Field>
  );
};

EditorField.propTypes = {
  ...Field.propTypes,
  ...Editor.propTypes,
};
